import React, { useState, useEffect } from 'react';
import api from '../api';
import { useAuth } from '../context/AuthContext';

// Admin: configure email (SMTP) + notification settings
export default function Settings() {
  const { isAdmin } = useAuth();
  const [form, setForm] = useState(null);
  const [hasPassword, setHasPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg,  setMsg]  = useState(null);   // { type:'ok'|'err', text }
  const [testTo, setTestTo] = useState('');
  const [testing, setTesting] = useState(false);

  const load = async () => {
    try {
      const r = await api.get('/master/email-settings');
      const d = r.data || {};
      setHasPassword(!!d.has_password);
      setForm({
        enabled: !!d.enabled, host: d.host || '', port: d.port || 587,
        username: d.username || '', password: '',
        from_name: d.from_name || 'IT Support', from_email: d.from_email || '',
        notify_email: d.notify_email || '', sla_hours: d.sla_hours || 4, digest_cc: d.digest_cc || '',
        digest_enabled: d.digest_enabled !== false, digest_time: d.digest_time || '09:30',
        digest_after_days: d.digest_after_days ?? 1,
      });
    } catch(e) {}
  };
  useEffect(() => { if (isAdmin) load(); }, [isAdmin]);

  if (!isAdmin) return <div style={{ padding:40, color:'var(--text2)', textAlign:'center' }}>Admin access required</div>;
  if (!form) return <div style={{ padding:40, color:'var(--text2)' }}>Loading…</div>;

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setBusy(true); setMsg(null);
    try {
      await api.put('/master/email-settings', form);
      setMsg({ type:'ok', text:'Settings saved and applied.' });
      setForm(f => ({ ...f, password: '' }));   // clear the typed password field
      await load();
    } catch(e) { setMsg({ type:'err', text: e.response?.data?.error || 'Save failed' }); }
    finally { setBusy(false); }
  };

  const sendTest = async () => {
    if (!testTo.trim()) { setMsg({ type:'err', text:'Enter an email to send the test to.' }); return; }
    setTesting(true); setMsg(null);
    try {
      await api.post('/master/email-settings/test', { to: testTo.trim() });
      setMsg({ type:'ok', text:`Test email sent to ${testTo}. Check the inbox.` });
    } catch(e) { setMsg({ type:'err', text: e.response?.data?.error || 'Test failed — check the SMTP details.' }); }
    finally { setTesting(false); }
  };

  return (
    <div style={{ maxWidth:680 }}>
      <div style={{ marginBottom:20 }}>
        <h2 style={{ fontSize:18, fontWeight:600, marginBottom:4 }}>Settings — Email & Notifications</h2>
        <p style={{ fontSize:13, color:'var(--text2)' }}>Configure the mailbox the portal uses to send alerts. Saves apply immediately — no restart.</p>
      </div>

      {/* Enable toggle */}
      <div className="card" style={{ marginBottom:16 }}>
        <label className="opt" style={{ display:'flex', alignItems:'center', gap:10, cursor:'pointer', textTransform:'none', letterSpacing:0, fontSize:14, fontWeight:500 }}>
          <input type="checkbox" checked={form.enabled} onChange={e => set('enabled', e.target.checked)} style={{ width:16, height:16 }} />
          Enable email notifications
        </label>
        <div style={{ fontSize:12, color:'var(--text2)', marginTop:6 }}>
          When off, nothing is emailed (the portal still works fully). When on, fill the SMTP details below.
        </div>
      </div>

      {/* SMTP */}
      <div className="card" style={{ marginBottom:16 }}>
        <div style={{ fontSize:12, fontWeight:600, color:'var(--text2)', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:12 }}>SMTP Server</div>
        <div className="form-row">
          <div className="form-group">
            <label>SMTP Host</label>
            <input value={form.host} onChange={e => set('host', e.target.value)} placeholder="smtp.gmail.com" />
          </div>
          <div className="form-group" style={{ maxWidth:120 }}>
            <label>Port</label>
            <input type="number" value={form.port} onChange={e => set('port', e.target.value)} placeholder="587" />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Username (email)</label>
            <input value={form.username} onChange={e => set('username', e.target.value)} placeholder="itsupport@tipsglobeducate.com" />
          </div>
          <div className="form-group">
            <label>Password / App Password {hasPassword && <span style={{ fontSize:11, color:'var(--green)' }}>(saved)</span>}</label>
            <input type="password" value={form.password} onChange={e => set('password', e.target.value)}
              placeholder={hasPassword ? '•••••••• (leave blank to keep)' : '16-char Google App Password'} />
          </div>
        </div>
        <div style={{ fontSize:11, color:'var(--text2)' }}>
          Google Workspace: enable 2-Step Verification on the account, then create an <b>App Password</b> (Security → App passwords) and paste it here. Host <code>smtp.gmail.com</code>, port <code>587</code>.
        </div>
      </div>

      {/* Sender + notifications */}
      <div className="card" style={{ marginBottom:16 }}>
        <div style={{ fontSize:12, fontWeight:600, color:'var(--text2)', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:12 }}>Sender & Alerts</div>
        <div className="form-row">
          <div className="form-group">
            <label>From Name</label>
            <input value={form.from_name} onChange={e => set('from_name', e.target.value)} placeholder="IT Support" />
          </div>
          <div className="form-group">
            <label>From Email</label>
            <input value={form.from_email} onChange={e => set('from_email', e.target.value)} placeholder="itsupport@tipsglobeducate.com" />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>IT Alert Inbox <span style={{ fontSize:11, color:'var(--text2)' }}>(gets registration / ticket / SLA alerts)</span></label>
            <input value={form.notify_email} onChange={e => set('notify_email', e.target.value)} placeholder="itsupport@tipsglobeducate.com" />
          </div>
          <div className="form-group" style={{ maxWidth:160 }}>
            <label>SLA Hours</label>
            <input type="number" min={1} value={form.sla_hours} onChange={e => set('sla_hours', e.target.value)} />
            <div style={{ fontSize:11, color:'var(--text2)', marginTop:4 }}>Alert if a ticket is unattended this long.</div>
          </div>
        </div>
        {/* Pending-task digest — one mail per IT person listing their open tasks */}
        <div style={{ marginTop:8, padding:'12px 14px', background:'var(--bg3)', border:'1px solid var(--border)', borderRadius:'var(--radius)' }}>
          <label className="opt" style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, textTransform:'none', letterSpacing:0, fontWeight:600, marginBottom:10 }}>
            <input type="checkbox" checked={!!form.digest_enabled} onChange={e => set('digest_enabled', e.target.checked)} />
            📋 Send the daily pending-task digest
          </label>
          <div className="form-row">
            <div className="form-group" style={{ maxWidth:150 }}>
              <label>Send at</label>
              <input type="time" value={form.digest_time} onChange={e => set('digest_time', e.target.value)} />
              <div style={{ fontSize:11, color:'var(--text2)', marginTop:4 }}>IST · Mon–Sat</div>
            </div>
            <div className="form-group" style={{ maxWidth:170 }}>
              <label>Pending after</label>
              <input type="number" min={0} max={90} value={form.digest_after_days}
                onChange={e => set('digest_after_days', e.target.value)} />
              <div style={{ fontSize:11, color:'var(--text2)', marginTop:4 }}>
                days since created (0 = same day)
              </div>
            </div>
            <div className="form-group">
              <label>Digest CC</label>
              <input value={form.digest_cc} onChange={e => set('digest_cc', e.target.value)} placeholder="you@tipsglobeducate.com" />
              <div style={{ fontSize:11, color:'var(--text2)', marginTop:4 }}>
                You get a copy of each IT person's list. Comma-separate for more than one; blank = IT Alert Inbox.
              </div>
            </div>
          </div>
        </div>
      </div>

      {msg && <div className={msg.type === 'ok' ? '' : 'err-msg'} style={msg.type === 'ok' ? { color:'var(--green)', fontSize:13, marginBottom:12 } : { marginBottom:12 }}>{msg.text}</div>}

      <div className="flex-gap" style={{ gap:10, marginBottom:24 }}>
        <button className="btn btn-primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save Settings'}</button>
      </div>

      {/* Test */}
      <div className="card">
        <div style={{ fontSize:12, fontWeight:600, color:'var(--text2)', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:10 }}>Send a Test Email</div>
        <div className="flex-gap" style={{ gap:10, alignItems:'flex-end' }}>
          <div className="form-group" style={{ flex:1, marginBottom:0 }}>
            <label>Send test to</label>
            <input value={testTo} onChange={e => setTestTo(e.target.value)} placeholder="you@tipsglobeducate.com" />
          </div>
          <button className="btn btn-secondary" onClick={sendTest} disabled={testing}>{testing ? 'Sending…' : 'Send Test'}</button>
        </div>
        <div style={{ fontSize:11, color:'var(--text2)', marginTop:8 }}>Save your settings first, then send a test to confirm it works.</div>
      </div>

      <SsoConfig />
      <GoogleWorkspaceConfig />
      <CalendarSyncConfig />
      <QuickLinksConfig />
      <AiConfig />
      <RemoteSupportConfig />
      <EscalationConfig />
    </div>
  );
}

// ── Google Workspace admin (staff onboarding, phase 1) ───
// Read-only against Google: configuration, connection test and directory
// lookups. No account is created or changed from here yet.
function GoogleWorkspaceConfig() {
  const [d, setD] = useState(null);
  const [f, setF] = useState({ domain:'', impersonate_email:'', key_file:'', default_ou:'' });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [loadErr, setLoadErr] = useState('');

  const load = () => api.get('/gw/settings').then(r => {
    setLoadErr(''); setD(r.data);
    setF({ domain: r.data.domain || '', impersonate_email: r.data.impersonate_email || '',
           key_file: r.data.key_file || '', default_ou: r.data.default_ou || '' });
  }).catch(e => setLoadErr(e.response?.data?.error || 'Could not load Google Workspace settings'));
  useEffect(() => { load(); }, []);

  const save = async (patch = {}) => {
    setBusy(true); setMsg('');
    try { await api.put('/gw/settings', { ...f, ...patch }); setMsg('✓ Saved'); setTimeout(()=>setMsg(''),2500); load(); }
    catch (e) { setMsg(e.response?.data?.error || 'Could not save'); }
    finally { setBusy(false); }
  };

  const test = async () => {
    setBusy(true); setMsg('Testing…');
    try { const r = await api.post('/gw/test', {}); setMsg(`✓ Connected to Google · ${r.data.users_visible ? 'directory readable' : 'no users returned'}`); load(); }
    catch (e) { setMsg(e.response?.data?.error || 'Connection failed'); load(); }
    finally { setBusy(false); }
  };
  if (!d) return (
    <div className="card" style={{ marginBottom:16 }}>
      <b style={{ fontSize:14 }}>👥 Google Workspace (staff onboarding)</b>
      <div style={{ fontSize:12.5, color:loadErr?'var(--red)':'var(--text2)', marginTop:8 }}>{loadErr || 'Loading…'}</div>
    </div>
  );

  const keyOk = d.key_present;
  return (
    <div className="card" style={{ marginBottom:16 }}>
      <div className="flex-gap" style={{ justifyContent:'space-between', alignItems:'center', marginBottom:8, flexWrap:'wrap', gap:8 }}>
        <b style={{ fontSize:14 }}>👥 Google Workspace (staff onboarding)</b>
        <label style={{ fontSize:12, display:'flex', alignItems:'center', gap:6 }}>
          <input type="checkbox" checked={!!d.enabled} onChange={e => save({ enabled: e.target.checked })} />
          Enabled
        </label>
      </div>
      <div style={{ fontSize:11.5, color:'var(--text2)', marginBottom:10 }}>
        Creates staff accounts and updates group membership during onboarding. Google Drive folders are
        created outside the portal — the induction checklist records the link. The portal never deletes
        an account and never stores a password.
      </div>

      {/* Key file status — the thing that is wrong 90% of the time */}
      <div style={{ padding:'8px 10px', borderRadius:6, marginBottom:10, fontSize:12,
        background: keyOk ? 'var(--bg3)' : 'rgba(220,70,70,.08)',
        border: `1px solid ${keyOk ? 'var(--border)' : 'var(--red)'}` }}>
        {keyOk ? <>
          <b style={{ color:'var(--green)' }}>✓ Service-account key found</b>
          <div style={{ color:'var(--text2)', marginTop:3 }}>
            Client email <span className="mono">{d.key_client_email}</span> — authorise domain-wide
            delegation for this client ID in the Admin console with the scopes listed below.
          </div>
        </> : <>
          <b style={{ color:'var(--red)' }}>Service-account key not readable</b>
          <div style={{ color:'var(--text2)', marginTop:3 }}>{d.key_error}</div>
          <div style={{ color:'var(--text2)', marginTop:3 }}>
            Place the JSON key on the server at <span className="mono">{d.key_file}</span> (the host folder
            mounted read-only as <span className="mono">/app/secrets</span>).
          </div>
        </>}
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
        <div className="form-group"><label>Workspace domain</label>
          <input value={f.domain} onChange={e => setF(s=>({...s,domain:e.target.value}))} placeholder="tipsglobeducate.com" /></div>
        <div className="form-group"><label>Impersonated admin</label>
          <input value={f.impersonate_email} onChange={e => setF(s=>({...s,impersonate_email:e.target.value}))} placeholder="portal-automation@…" />
          <span style={{ fontSize:11, color:'var(--text2)' }}>A dedicated admin account, not a person's.</span></div>
        <div className="form-group"><label>Key file path (on the server)</label>
          <input value={f.key_file} onChange={e => setF(s=>({...s,key_file:e.target.value}))} /></div>
        <div className="form-group"><label>Fallback OU (optional)</label>
          <input value={f.default_ou} onChange={e => setF(s=>({...s,default_ou:e.target.value}))} placeholder="/Staff" /></div>
      </div>

      <div style={{ fontSize:11, color:'var(--text2)', margin:'4px 0 8px' }}>
        Scopes to authorise: {d.scopes?.map(s => <span key={s} className="mono" style={{ marginRight:8 }}>{s.replace('https://www.googleapis.com/auth/','')}</span>)}
      </div>

      {/* Simulation. The whole point is that leaving it is a deliberate act,
          so it is a separate, clearly-worded block rather than one more tick
          box among the connection fields. */}
      <div style={{ padding:'10px 12px', borderRadius:6, marginBottom:10,
        background: d.simulation ? 'var(--bg3)' : 'rgba(220,70,70,.07)',
        border: `1px solid ${d.simulation ? 'var(--border)' : 'var(--red)'}` }}>
        <label className="opt" style={{ alignItems:'flex-start' }}>
          <input type="checkbox" checked={!d.simulation} style={{ marginTop:3 }}
            onChange={e => save({ simulation: !e.target.checked })} />
          <span>
            <b>{d.simulation ? 'Simulation — nothing is created in Google' : 'Live — accounts are really created in Google'}</b>
            <div style={{ fontSize:11.5, color:'var(--text2)', marginTop:2 }}>
              {d.simulation
                ? 'Every step works and is recorded, but no account or group membership reaches Google. Tick this box to go live.'
                : 'Creating an account on an onboarding record will create a real Workspace account and add real group members. Untick to go back to testing.'}
            </div>
            {d.simulation && !d.last_test_ok && (
              <div style={{ fontSize:11.5, color:'var(--text2)', marginTop:3 }}>
                Test Connection has to pass before this can be turned off.
              </div>
            )}
          </span>
        </label>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
        <div className="form-group"><label>Policy quiz pass mark (%)</label>
          <input type="number" min={1} max={100} defaultValue={d.quiz_pass_pct ?? 80}
            onBlur={e => Number(e.target.value) !== d.quiz_pass_pct && save({ quiz_pass_pct: e.target.value })} /></div>
        <div className="form-group"><label>Portal welcome email</label>
          <label className="opt" style={{ marginTop:6 }}>
            <input type="checkbox" checked={!!d.portal_welcome_enabled}
              onChange={e => save({ portal_welcome_enabled: e.target.checked })} />
            <span>Also send the portal login welcome</span>
          </label>
          <span style={{ fontSize:11, color:'var(--text2)' }}>
            Separate from the school welcome. Leave off until the portal is launched to all staff.
          </span></div>
      </div>

      {d.last_test_at && (
        <div style={{ fontSize:11.5, marginBottom:8, color: d.last_test_ok ? 'var(--green)' : 'var(--red)' }}>
          Last test {new Date(d.last_test_at).toLocaleString()} — {d.last_test_ok ? 'OK' : d.last_test_error}
        </div>
      )}
      {msg && <div style={{ fontSize:12.5, marginBottom:8, color: msg.startsWith('✓')?'var(--green)':msg==='Testing…'?'var(--text2)':'var(--red)' }}>{msg}</div>}

      <div className="flex-gap" style={{ gap:8, flexWrap:'wrap' }}>
        <button className="btn btn-primary" onClick={() => save()} disabled={busy}>Save</button>
        <button className="btn btn-secondary" onClick={test} disabled={busy}>Test Connection</button>
      </div>

      {/* OU and group mapping moved to Master → Google OU Mapping: a campus has
          several OUs (Admin, Academic, PA & PE), and group addresses already
          live in the Group IDs master. */}
      <div style={{ marginTop:10, fontSize:11.5, color:'var(--text2)' }}>
        Campus organizational units are managed in <b>Master → Google OU Mapping</b>.
        Group addresses come from the existing <b>Group IDs</b> master.
      </div>
    </div>
  );
}

// ── Escalation ladder (Module 9) ─────────────────────────
const ESC_TARGETS = [['assignee','Assigned engineer'],['it_head','IT Head'],['hos','HOS (campus)'],['management','Management'],['admin','Admin']];
const targetLabel = t => (ESC_TARGETS.find(x=>x[0]===t)?.[1]) || t;
function EscalationConfig() {
  const [rows, setRows] = useState([]);
  const [nw, setNw] = useState({ after_days:'', notify_target:'it_head', set_escalated:true });
  const [msg, setMsg] = useState('');
  const load = () => api.get('/master/escalation-rules').then(r => setRows(r.data)).catch(()=>{});
  useEffect(() => { load(); }, []);
  const flash = m => { setMsg(m); setTimeout(()=>setMsg(''), 2500); };
  const add = async () => {
    if (!(parseInt(nw.after_days) > 0)) { flash('Enter a positive day count'); return; }
    try { await api.post('/master/escalation-rules', nw); setNw({ after_days:'', notify_target:'it_head', set_escalated:true }); load(); }
    catch (e) { flash(e.response?.data?.error || 'Failed'); }
  };
  const upd = async (id, patch) => { try { await api.put(`/master/escalation-rules/${id}`, patch); load(); } catch(e){ flash('Failed'); } };
  const del = async id => { if (window.confirm('Delete this escalation step?')) { await api.delete(`/master/escalation-rules/${id}`); load(); } };
  const runNow = async () => { try { const r = await api.post('/master/escalation-rules/run'); flash(`✓ Ran — ${r.data.fired} escalation(s) fired`); }
    catch (e) { flash('Run failed'); } };

  return (
    <div className="card" style={{ marginTop:24 }}>
      <div className="flex-gap" style={{ justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
        <div style={{ fontSize:12, fontWeight:600, color:'var(--text2)', textTransform:'uppercase', letterSpacing:'0.05em' }}>Escalation Ladder</div>
        <button className="btn btn-secondary btn-sm" onClick={runNow}>▶ Run now</button>
      </div>
      <div style={{ fontSize:11, color:'var(--text2)', marginBottom:12 }}>
        After a task sits at its status for N days without change, notify the target (and optionally flag it Escalated). The clock resets whenever the status changes.
      </div>
      <div className="table-wrap"><table>
        <thead><tr><th>After (days)</th><th>Notify</th><th>Flag Escalated</th><th>Active</th><th></th></tr></thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.id}>
              <td><input type="number" min="1" value={r.after_days} onChange={e => upd(r.id, { after_days:e.target.value })} style={{ width:70, padding:'4px 6px' }} /></td>
              <td><select value={r.notify_target} onChange={e => upd(r.id, { notify_target:e.target.value })} style={{ padding:'4px 6px' }}>
                {ESC_TARGETS.map(([v,l]) => <option key={v} value={v}>{l}</option>)}</select></td>
              <td><input type="checkbox" checked={r.set_escalated} onChange={e => upd(r.id, { set_escalated:e.target.checked })} /></td>
              <td><input type="checkbox" checked={r.active} onChange={e => upd(r.id, { active:e.target.checked })} /></td>
              <td><button className="btn btn-danger btn-sm" onClick={() => del(r.id)}>×</button></td>
            </tr>
          ))}
          <tr>
            <td><input type="number" min="1" placeholder="days" value={nw.after_days} onChange={e => setNw(s => ({ ...s, after_days:e.target.value }))} style={{ width:70, padding:'4px 6px' }} /></td>
            <td><select value={nw.notify_target} onChange={e => setNw(s => ({ ...s, notify_target:e.target.value }))} style={{ padding:'4px 6px' }}>
              {ESC_TARGETS.map(([v,l]) => <option key={v} value={v}>{l}</option>)}</select></td>
            <td><input type="checkbox" checked={nw.set_escalated} onChange={e => setNw(s => ({ ...s, set_escalated:e.target.checked }))} /></td>
            <td colSpan="2"><button className="btn btn-primary btn-sm" onClick={add}>+ Add step</button></td>
          </tr>
        </tbody>
      </table></div>
      {msg && <div style={{ fontSize:13, marginTop:8, color: msg.startsWith('✓')?'var(--green)':'var(--red)' }}>{msg}</div>}
    </div>
  );
}

// ── AI self-service assistant ────────────────────────────
// Reviewed 21 Oct 2026 against the logged answers. qwen2.5:0.5b was removed:
// it was labelled "fastest" and produced most of the wrong answers in the
// review — too small to stay grounded in the documents. Smallest first.
const MODEL_PRESETS = [
  { id: 'qwen3:1.7b',  label: 'qwen3:1.7b — fastest', hint: 'Quickest usable option. Use if qwen3:4b feels too slow on CPU.' },
  { id: 'llama3.2:1b', label: 'llama3.2:1b — fast', hint: 'Fast, but noticeably weaker than the 3–4B models at following the guides.' },
  { id: 'qwen3:4b',    label: 'qwen3:4b — recommended', hint: 'Best balance on CPU. Expect roughly 20–30s per answer; it streams as it writes.' },
  { id: 'llama3.2',    label: 'llama3.2 (3B) — proven', hint: 'Every highly-rated answer in the review came from this model. Similar speed to qwen3:4b.' },
  { id: 'qwen3:8b',    label: 'qwen3:8b — best quality (GPU only)', hint: 'Best answers, but too slow on CPU — use once you have a GPU.' },
];

function AiConfig() {
  const [s, setS] = useState(null);
  const [key, setKey] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  useEffect(() => { api.get('/ai/settings').then(r => setS(r.data)).catch(()=>{}); }, []);
  if (!s) return null;
  const set = (k,v) => setS(p => ({ ...p, [k]:v }));
  const save = async () => {
    setBusy(true); setMsg('');
    try {
      const body = { enabled: s.enabled, model: s.model, base_url: s.base_url, system_extra: s.system_extra };
      if (key.trim()) body.api_key = key.trim();
      await api.put('/ai/settings', body); setKey(''); setMsg('✓ Saved'); setTimeout(()=>setMsg(''),2000);
    } catch(e) { setMsg('Save failed'); } finally { setBusy(false); }
  };
  const test = async () => {
    setBusy(true); setMsg('Testing…');
    try { const r = await api.post('/ai/test', {}); setMsg('✓ Connected · model replied: ' + (r.data.reply || 'OK')); }
    catch(e) { setMsg('✗ ' + (e.response?.data?.error || 'Test failed')); } finally { setBusy(false); }
  };
  return (
    <div className="card" style={{ marginTop:24 }}>
      <div style={{ fontSize:12, fontWeight:600, color:'var(--text2)', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:10 }}>AI Self-Service Assistant (Llama)</div>
      <label className="opt" style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, textTransform:'none', letterSpacing:0, marginBottom:12 }}>
        <input type="checkbox" checked={!!s.enabled} onChange={e => set('enabled', e.target.checked)} /> Enable the AI answer box on the staff Home page
      </label>
      <div className="form-row">
        <div className="form-group"><label>Endpoint (Ollama / OpenAI-compatible)</label>
          <input value={s.base_url || ''} onChange={e => set('base_url', e.target.value)} placeholder="http://ollama:11434" /></div>
        <div className="form-group"><label>Model</label>
          <select value={MODEL_PRESETS.some(m => m.id === s.model) ? s.model : '__custom'}
            onChange={e => set('model', e.target.value === '__custom' ? '' : e.target.value)}>
            {MODEL_PRESETS.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
            <option value="__custom">Custom…</option>
          </select>
          {!MODEL_PRESETS.some(m => m.id === s.model) &&
            <input style={{ marginTop:6 }} value={s.model || ''} onChange={e => set('model', e.target.value)} placeholder="e.g. mistral:7b-instruct" />}
          <span style={{ fontSize:11, color:'var(--text2)' }}>{MODEL_PRESETS.find(m => m.id === s.model)?.hint || 'Must be pulled on the server first.'}</span>
        </div>
      </div>
      <div className="form-group"><label>API Key <span style={{ fontSize:11, color:'var(--text2)' }}>(only for hosted providers; leave blank for local Ollama)</span> {s.has_api_key && <span style={{ color:'var(--green)', fontSize:11 }}>· stored ✓</span>}</label>
        <input type="password" value={key} onChange={e => setKey(e.target.value)} placeholder="leave blank for local Llama" /></div>
      <div className="form-group"><label>Extra guidance (optional)</label>
        <textarea rows={2} value={s.system_extra || ''} onChange={e => set('system_extra', e.target.value)} placeholder="e.g. Wi-Fi SSID is TIPS-Staff; printers use PaperCut; escalate CCTV to the campus IT lead." /></div>
      <div style={{ fontSize:11, color:'var(--text2)', marginBottom:8 }}>Runs a local Llama model via Ollama on the server — private, no per-question cost. Install a model once with <span className="mono">docker exec itportal_ollama ollama pull qwen3:4b</span> — a model must be pulled on the server before it can be selected here.</div>
      {msg && <div style={{ fontSize:13, color: msg.startsWith('✓')?'var(--green)':msg==='Testing…'?'var(--text2)':'var(--red)', marginBottom:8 }}>{msg}</div>}
      <div className="flex-gap" style={{ gap:8 }}>
        <button className="btn btn-primary" onClick={save} disabled={busy}>{busy?'…':'Save AI Settings'}</button>
        <button className="btn btn-secondary" onClick={test} disabled={busy}>Test Connection</button>
      </div>
    </div>
  );
}

// ── Remote Support (RustDesk self-hosted server) ─────────
function RemoteSupportConfig() {
  const [s, setS] = useState(null);
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get('/remote/settings').then(r => setS(r.data || {})).catch(()=>setS({})); }, []);
  if (!s) return null;
  const set = (k, v) => setS(p => ({ ...p, [k]: v }));
  const save = async () => {
    setBusy(true); setMsg('');
    try { await api.put('/remote/settings', s); setMsg('✓ Saved'); setTimeout(()=>setMsg(''),2000); }
    catch (e) { setMsg(e.response?.data?.error || 'Save failed'); } finally { setBusy(false); }
  };
  return (
    <div className="card" style={{ marginTop:24 }}>
      <div style={{ fontSize:12, fontWeight:600, color:'var(--text2)', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:10 }}>Remote Support (RustDesk)</div>
      <div style={{ fontSize:11, color:'var(--text2)', marginBottom:12 }}>
        Your self-hosted RustDesk server address. The portal uses this for the “Connect” actions and shows it to
        users &amp; IT. Installing and configuring the RustDesk client on each machine is done on the server / device itself.
      </div>
      <div className="form-group"><label>ID / Server Host</label>
        <input value={s.server_host || ''} onChange={e => set('server_host', e.target.value)} placeholder="itsupport.tipsglobeducate.com" /></div>
      {msg && <div style={{ fontSize:13, color: msg.startsWith('✓')?'var(--green)':'var(--red)', marginBottom:8 }}>{msg}</div>}
      <button className="btn btn-primary" onClick={save} disabled={busy}>{busy?'…':'Save RustDesk Settings'}</button>
    </div>
  );
}

// ── Quick links (HRMS / ERP) ─────────────────────────────
function QuickLinksConfig() {
  const [hrms, setHrms] = useState('');
  const [erp, setErp] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  useEffect(() => {
    api.get('/master/links').then(r => { setHrms(r.data.hrms_url || ''); setErp(r.data.erp_links || []); }).catch(()=>{});
  }, []);
  const save = async () => {
    setBusy(true); setMsg('');
    try { await api.put('/master/links', { hrms_url: hrms, erp_links: erp.filter(e => e.label && e.url) }); setMsg('✓ Saved'); setTimeout(()=>setMsg(''),2000); }
    catch(e) { setMsg('Save failed'); } finally { setBusy(false); }
  };
  return (
    <div className="card" style={{ marginTop:24 }}>
      <div style={{ fontSize:12, fontWeight:600, color:'var(--text2)', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:10 }}>Quick Links (HRMS / ERP)</div>
      <div className="form-group"><label>HRMS Link (single)</label>
        <input value={hrms} onChange={e => setHrms(e.target.value)} placeholder="https://hrms.tipsglobeducate.com" /></div>
      <label>ERP Links (location-wise)</label>
      <div style={{ marginBottom:10 }}>
        {erp.map((row, i) => (
          <div key={i} className="flex-gap" style={{ gap:8, marginBottom:6 }}>
            <input value={row.label} placeholder="Location (e.g. Chennai)" onChange={e => setErp(a => a.map((x,j)=>j===i?{...x,label:e.target.value}:x))} style={{ flex:1 }} />
            <input value={row.url} placeholder="https://erp-chennai..." onChange={e => setErp(a => a.map((x,j)=>j===i?{...x,url:e.target.value}:x))} style={{ flex:2 }} />
            <button className="btn btn-secondary btn-sm" onClick={() => setErp(a => a.filter((_,j)=>j!==i))}>×</button>
          </div>
        ))}
        <button className="btn btn-secondary btn-sm" onClick={() => setErp(a => [...a, { label:'', url:'' }])}>+ Add ERP location</button>
      </div>
      {msg && <div style={{ fontSize:13, color: msg.startsWith('✓')?'var(--green)':'var(--red)', marginBottom:8 }}>{msg}</div>}
      <button className="btn btn-primary" onClick={save} disabled={busy}>{busy?'Saving…':'Save Links'}</button>
    </div>
  );
}

// ── Single Sign-On (Google) ──────────────────────────────
// ── Calendar sync: the Google OAuth client, entered once ────────────────────
// Each IT person then connects their OWN calendar from the Calendar page; this
// screen only holds the credentials that make that possible.
function CalendarSyncConfig() {
  const [d, setD] = useState(null);
  const [f, setF] = useState({ oauth_client_id:'', oauth_client_secret:'', sync_days_back:7, sync_days_ahead:30 });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const [loadErr, setLoadErr] = useState('');

  const load = () => api.get('/calendar-sync/settings').then(r => {
    setLoadErr('');
    setD(r.data);
    setF(s => ({ ...s, oauth_client_id: r.data.oauth_client_id || '',
      sync_days_back: r.data.sync_days_back ?? 7, sync_days_ahead: r.data.sync_days_ahead ?? 30 }));
  }).catch(e => {
    // Say so on the page. Swallowing this hid the whole section and looked
    // like the feature had never been deployed.
    const s = e.response?.status;
    setLoadErr(s === 404
      ? 'The portal backend does not have the calendar routes yet — the backend image is older than this page.'
      : s === 500
        ? 'The calendar tables are missing — the calendar migration has not been applied.'
        : (e.response?.data?.error || 'Could not load calendar settings.'));
  });
  useEffect(() => { load(); }, []);

  const save = async (patch = {}) => {
    setBusy(true); setMsg('');
    try {
      await api.put('/calendar-sync/settings', { ...f, ...patch });
      setF(s => ({ ...s, oauth_client_secret: '' }));   // never keep it in the page
      setMsg('✓ Saved'); setTimeout(() => setMsg(''), 2500); load();
    } catch (e) { setMsg(e.response?.data?.error || 'Could not save'); }
    finally { setBusy(false); }
  };

  if (!d) return (
    <div className="card" style={{ marginBottom:16 }}>
      <b style={{ fontSize:14 }}>📅 Calendar Sync (Google)</b>
      <div style={{ fontSize:12.5, color:loadErr ? 'var(--red)' : 'var(--text2)', marginTop:8 }}>
        {loadErr || 'Loading…'}
      </div>
    </div>
  );
  return (
    <div className="card" style={{ marginBottom:16 }}>
      <div className="flex-gap" style={{ justifyContent:'space-between', alignItems:'center', marginBottom:8, flexWrap:'wrap', gap:8 }}>
        <b style={{ fontSize:14 }}>📅 Calendar Sync (Google)</b>
        <label className="opt">
          <input type="checkbox" checked={!!d.enabled}
            onChange={e => save({ enabled: e.target.checked })} />
          <span>Let IT staff connect their calendar</span>
        </label>
      </div>
      <p style={{ fontSize:12.5, color:'var(--text2)', marginBottom:10 }}>
        Each person connects their own work calendar from the Calendar page. They see
        their own entries; nobody sees anyone else's. Nothing becomes a ticket unless
        they press “Add to my tasks”.
      </p>

      <div className="form-row">
        <div className="form-group" style={{ flex:2 }}>
          <label>Google OAuth Client ID</label>
          <input value={f.oauth_client_id}
            onChange={e => setF(s => ({ ...s, oauth_client_id: e.target.value }))}
            placeholder="123456-abc.apps.googleusercontent.com" />
        </div>
        <div className="form-group" style={{ flex:2 }}>
          <label>Client Secret {d.has_secret && <span style={{ fontSize:11, color:'var(--green)' }}>— saved</span>}</label>
          {/* Write-only: typed in, never sent back. Leave blank to keep the
              stored one when changing anything else on this screen. */}
          <input type="password" value={f.oauth_client_secret}
            onChange={e => setF(s => ({ ...s, oauth_client_secret: e.target.value }))}
            placeholder={d.has_secret ? '•••••••• (leave blank to keep)' : 'paste the secret'} />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Sync days back</label>
          <input type="number" min={0} max={90} value={f.sync_days_back}
            onChange={e => setF(s => ({ ...s, sync_days_back: e.target.value }))} />
        </div>
        <div className="form-group">
          <label>Sync days ahead</label>
          <input type="number" min={1} max={180} value={f.sync_days_ahead}
            onChange={e => setF(s => ({ ...s, sync_days_ahead: e.target.value }))} />
        </div>
      </div>

      <div style={{ background:'var(--bg3)', borderRadius:8, padding:'9px 11px', fontSize:11.5,
        color:'var(--text2)', marginBottom:10 }}>
        Add this exact address to the OAuth client’s <b>Authorised redirect URIs</b> in
        Google Cloud Console, and enable the <b>Google Calendar API</b>:
        <div className="mono" style={{ marginTop:4, color:'var(--text)', wordBreak:'break-all' }}>{d.redirect_uri}</div>
      </div>

      <div className="flex-gap" style={{ gap:8, alignItems:'center' }}>
        <button className="btn btn-primary btn-sm" disabled={busy} onClick={() => save()}>
          {busy ? 'Saving…' : 'Save'}
        </button>
        {msg && <span style={{ fontSize:12.5, color: msg.startsWith('✓') ? 'var(--green)' : 'var(--red)' }}>{msg}</span>}
      </div>

      {(d.connected || []).length > 0 && (
        <div style={{ marginTop:12 }}>
          <div style={{ fontSize:11.5, fontWeight:600, color:'var(--text2)', textTransform:'uppercase', marginBottom:4 }}>
            Connected ({d.connected.length})
          </div>
          {d.connected.map((c, i) => (
            <div key={i} style={{ fontSize:12, color:'var(--text2)' }}>
              {c.full_name} — {c.connected_email}
              {c.last_synced_at && ` · ${new Date(c.last_synced_at).toLocaleString('en-IN', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' })}`}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SsoConfig() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  useEffect(() => { api.get('/master/sso-settings').then(r => setS(r.data)).catch(()=>{}); }, []);
  if (!s) return null;
  const save = async () => {
    setBusy(true); setMsg('');
    try { await api.put('/master/sso-settings', s); setMsg('✓ Saved'); setTimeout(()=>setMsg(''),2000); }
    catch(e) { setMsg('Save failed'); } finally { setBusy(false); }
  };
  return (
    <div className="card" style={{ marginTop:24 }}>
      <div style={{ fontSize:12, fontWeight:600, color:'var(--text2)', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:10 }}>Single Sign-On (Google)</div>
      <label className="opt" style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, textTransform:'none', letterSpacing:0, marginBottom:12 }}>
        <input type="checkbox" checked={!!s.enabled} onChange={e => setS(p=>({...p,enabled:e.target.checked}))} /> Enable "Sign in with Google"
      </label>
      <div className="form-group"><label>Google OAuth Client ID</label>
        <input value={s.client_id||''} onChange={e => setS(p=>({...p,client_id:e.target.value}))} placeholder="xxxxxxxx.apps.googleusercontent.com" /></div>
      <div className="form-row">
        <div className="form-group"><label>Allowed email domain</label>
          <input value={s.allowed_domain||''} onChange={e => setS(p=>({...p,allowed_domain:e.target.value}))} placeholder="tipsglobeducate.com" /></div>
        <div className="form-group">
          <label className="opt" style={{ display:'flex', alignItems:'center', gap:8, textTransform:'none', letterSpacing:0 }}>
            <input type="checkbox" checked={!!s.auto_provision} onChange={e => setS(p=>({...p,auto_provision:e.target.checked}))} /> Auto-create new users (pending approval)
          </label>
        </div>
      </div>
      <div style={{ fontSize:11, color:'var(--text2)', marginBottom:12 }}>
        Create an OAuth Client ID (Web) in Google Cloud Console with <span className="mono">https://itsupport.tipsglobeducate.com</span> as an authorized JavaScript origin, paste the Client ID here, set the domain, and Enable. No client secret needed.
      </div>
      {msg && <div style={{ fontSize:13, color: msg.startsWith('✓')?'var(--green)':'var(--red)', marginBottom:8 }}>{msg}</div>}
      <button className="btn btn-primary" onClick={save} disabled={busy}>{busy?'Saving…':'Save SSO Settings'}</button>
    </div>
  );
}
